/* The locale that FORMATS, which is not the locale that translates.
 *
 * The catalogue has `en` and `tr` — the two languages we have written. Dates,
 * numbers and currencies have hundreds of correct answers, and `en` alone is
 * the American one: a reader in London given the catalogue's bare `en` sees
 * "August 17, 2026" on every screen.
 *
 * So the language comes from the catalogue and the REGION comes from the
 * reader. `i18n.md` puts it as: locale is a user setting, never inferred from
 * country — which cuts both ways. A Turkish speaker in Germany gets Turkish,
 * and a British reader gets British ordering, and neither borrows the other's.
 */

import type { Locale } from '@eventuley/i18n';

/**
 * The most specific tag the reader asked for whose language is the active one.
 *
 * Falls back to the bare locale, which is always valid.
 */
export function formatLocaleFor(locale: Locale, languages: readonly string[]): string {
  for (const language of languages) {
    /* Folded with 'en-US', never the reader's locale: a BCP 47 tag is ASCII,
     * and folding ASCII with Turkish rules turns `TR` into `tr` correctly but
     * `TITLE`-shaped tags into dotless-i nonsense. `i18n.md` calls casing
     * without an explicit locale a defect; this is the safe explicit one. */
    const base = language.split('-')[0]?.toLocaleLowerCase('en-US');
    if (base === locale) return language;
  }
  return locale;
}
